import React, { Dispatch, FC, SetStateAction } from 'react';
import { VictoryAxis, VictoryChart, VictoryGroup, VictoryLine, VictoryScatter, VictoryVoronoiContainer } from 'victory';
import { ExtendedGraphData, MultiGraphData } from '../../estimates/useHistoricGraphData';
import { Spinner } from '../../../../../../common/share/ShareComponents';
import { colorFundMap, getGraphTheme } from './savingProfileGraphTheme';
import { formatAmount } from '../../../../../../common/util/Functions';
import { monthYearParse } from './graphUtils';
import './SavingProfileGraph.scss';

type HistoricDevelopmentProps = {
    graphData: MultiGraphData;
    selectedFunds: string[];
    setSelectedFunds: Dispatch<SetStateAction<string[]>>;
};

const EstimatesGraph: FC<HistoricDevelopmentProps> = ({ graphData, setSelectedFunds, selectedFunds }) => {
    const animationDuration = 2_000
    const { graphDataSet, pending, edgeDates, ticks } = graphData;

    const getEstimateLine = (graphData: ExtendedGraphData) => {
        const points = graphData.data.series[0].points.map((points) => ({
            ...points,
            isin: graphData.isin,
            name: graphData.name,
        }));
        const color = colorFundMap[graphData.isin as keyof typeof colorFundMap];
        return (
            <VictoryGroup key={`group-${graphData.isin}`}>
                <VictoryLine
                    key={`line-${graphData.isin}`}
                    data={points}
                    style={{
                        data: {
                            stroke: color,
                            strokeWidth: selectedFunds.includes(graphData.isin) ? 3 : 2,
                        },
                    }}
                    animate={{ onLoad: { duration: 1 }, duration: animationDuration }}
                />
                <VictoryScatter
                    key={`scatter-${graphData.isin}`}
                    data={[points[points.length - 1]]}
                    size={5}
                    style={{
                        data: {
                            fill: color,
                            opacity: ({ datum }) => datum.opacity || 1,
                        },
                    }}
                    animate={{
                        onLoad: {
                            duration: animationDuration,
                            before: () => ({
                                opacity: 0.01,
                            }),
                        },
                        duration: animationDuration,
                    }}
                />
            </VictoryGroup>
        );
    };

    const EstimateLineLabel = (props: { text?: string[]; x?: number; y?: number }) => {
        const { text, x, y } = props;
        const dataSets = (text || []).map((substring: string) => JSON.parse(substring));

        if (!x || !y || !dataSets.length) return null;

        return (
            <foreignObject width="150" height="35" x={x - 140} y={y - 18}>
                <div className="label-wrapper">
                    <div className="data-point-label">{formatAmount(dataSets[0].value * 10, true)}</div>
                    <div
                        className="data-point-outer"
                        style={{ color: colorFundMap[dataSets[0].isin as keyof typeof colorFundMap] }}
                    >
                        <div className="data-point-inner" />
                    </div>
                </div>
            </foreignObject>
        );
    };

    return (
        <div className="saving-profile-graph -estimate">
            <div className="-content">
                <div className="graph-wrapper -estimate">
                    {pending && <Spinner />}
                    {!pending && graphDataSet && edgeDates && (
                        <VictoryChart
                            theme={getGraphTheme({ estimate: true })}
                            domainPadding={{ x: 2, y: 2 }}
                            containerComponent={
                                <VictoryVoronoiContainer
                                    radius={7}
                                    onActivated={(points) => setSelectedFunds(points.map((point) => point.isin))}
                                    labels={(d) =>
                                        JSON.stringify({
                                            value: d.datum.y,
                                            isin: d.datum.isin,
                                            name: d.datum.name,
                                        })
                                    }
                                    labelComponent={<EstimateLineLabel />}
                                />
                            }
                        >
                            <VictoryAxis
                                dependentAxis
                                tickValues={ticks}
                                tickFormat={(tick) => formatAmount(tick * 10, true)}
                            />
                            <VictoryAxis crossAxis tickValues={edgeDates} tickFormat={monthYearParse} />

                            {graphDataSet.map(getEstimateLine)}
                        </VictoryChart>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EstimatesGraph;
